import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  UserPlus, 
  Store, 
  Package, 
  CreditCard, 
  Truck, 
  HeadphonesIcon,
  ArrowRight,
  CheckCircle2,
  Wallet,
  ShieldCheck,
  Share2
} from 'lucide-react';
import { WhatsAppIcon } from '@/components/ui/whatsapp-icon';

const steps = [
  {
    icon: UserPlus,
    title: '1. Create Your Account',
    description: 'Sign up for free and select "Seller" as your role to get started.',
    details: [
      'Provide your email and create a password',
      'Choose "Seller" during role selection',
      'Complete your business profile'
    ]
  },
  {
    icon: Store,
    title: '2. Set Up Your Store',
    description: 'Customize your seller profile with your business details.',
    details: [
      'Add your business name and description',
      'Upload a profile picture or logo',
      'Set up your WhatsApp for customer inquiries'
    ]
  },
  {
    icon: Package,
    title: '3. List Your Products',
    description: 'Add products with photos, descriptions, and pricing.',
    details: [
      'Upload high-quality product images',
      'Write compelling descriptions',
      'Set competitive prices and stock levels'
    ]
  },
  {
    icon: Share2,
    title: '4. Share to WhatsApp Status',
    description: 'Promote your products by sharing them on WhatsApp Status and groups.',
    details: [
      'Click the share button on any product',
      'Share directly to your WhatsApp Status',
      'Send product links to groups and contacts',
      'Rich previews show product image and price'
    ]
  },
  {
    icon: CreditCard,
    title: '5. Receive Orders & Payments',
    description: 'Get notified when customers place orders. Payments are held securely.',
    details: [
      'Instant notifications for new orders',
      'Secure escrow payment protection',
      'Multiple payment options for buyers'
    ]
  },
  {
    icon: Truck,
    title: '6. Fulfill & Ship Orders',
    description: 'Process orders and arrange delivery to your customers.',
    details: [
      'Use integrated delivery partners or self-deliver',
      'Provide tracking information',
      'Communicate with buyers via WhatsApp'
    ]
  },
  {
    icon: Wallet,
    title: '7. Get Paid',
    description: 'Funds are released to your account after successful delivery.',
    details: [
      'Add your bank account details',
      'Request withdrawals anytime',
      'Track your earnings in the dashboard'
    ]
  }
];

const benefits = [
  { icon: ShieldCheck, text: 'Secure escrow payment protection' },
  { icon: Store, text: 'Free store setup - no monthly fees to start' },
  { icon: HeadphonesIcon, text: 'Dedicated seller support' },
  { icon: Truck, text: 'Integrated delivery options' }
];

const faqs = [
  {
    question: 'How much does it cost to sell on SabiTradeNG?',
    answer: 'Signing up and listing products is completely free. We only charge a small commission when you make a sale. There are no monthly fees or hidden charges to get started.'
  },
  {
    question: 'How do I get paid for my sales?',
    answer: 'Payments are held securely in escrow until the buyer confirms delivery. Once confirmed, funds are released to your available balance. You can then withdraw to your bank account at any time from your seller dashboard.'
  },
  {
    question: 'What products can I sell?',
    answer: 'You can sell a wide variety of products including food items, fashion, electronics, home goods, beauty products, and more. All products must comply with our terms of service and Nigerian commerce laws.'
  },
  {
    question: 'How does the WhatsApp sharing feature work?',
    answer: 'Each product has a share button that generates a special link with a rich preview (image, title, price). You can share this link to your WhatsApp Status, groups, or individual contacts. When someone clicks the link, they\'re taken directly to the product page ready to buy.'
  },
  {
    question: 'How do I handle deliveries?',
    answer: 'You have two options: use our integrated delivery partners who handle pickup and delivery, or manage deliveries yourself (self-delivery). You can set your preferred delivery method and fees in your seller settings.'
  },
  {
    question: 'What if a buyer wants to return a product?',
    answer: 'Buyers can request returns within the specified return window. You\'ll be notified of return requests and can communicate with the buyer to resolve issues. Our support team is available to help mediate if needed.'
  },
  {
    question: 'Can I manage multiple WhatsApp numbers?',
    answer: 'Yes! You can add up to 5 WhatsApp numbers for different purposes like Sales, Support, and Orders. Manage them in your WhatsApp Settings page from the seller dashboard.'
  },
  {
    question: 'How do I increase my sales?',
    answer: 'Use high-quality product photos, write detailed descriptions, price competitively, and actively share your products to WhatsApp Status and groups. Responding quickly to customer inquiries and maintaining good ratings also helps attract more buyers.'
  },
  {
    question: 'Is my money safe on the platform?',
    answer: 'Absolutely. We use a secure escrow system where buyer payments are held safely until delivery is confirmed. This protects both you and the buyer. Funds are never at risk and can only be released upon successful delivery.'
  },
  {
    question: 'How do I contact support?',
    answer: 'You can reach our seller support team through the Support Center in your dashboard, via the Contact page, or by creating a support ticket. We typically respond within 24 hours.'
  }
];

export default function HowToUse() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 via-background to-accent/10 py-16 md:py-24">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
            Start Selling in Minutes
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Join thousands of sellers growing their business on our platform. 
            It's easy, secure, and free to get started.
          </p>
          <Link to="/auth?mode=signup">
            <Button size="lg" className="gap-2">
              Create Seller Account <ArrowRight className="h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="bg-muted/50 py-6 border-y">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-2 justify-center">
                <benefit.icon className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">
            How It Works
          </h2>
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {steps.map((step, index) => (
              <Card key={index} className="relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-primary/5 rounded-bl-full" />
                <CardHeader>
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <step.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{step.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">{step.description}</p>
                  <ul className="space-y-2">
                    {step.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* WhatsApp Tip Section */}
      <section className="bg-[hsl(var(--whatsapp))]/10 py-12 border-y">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-6 max-w-4xl mx-auto">
            <div className="w-16 h-16 rounded-full bg-[hsl(var(--whatsapp))]/20 flex items-center justify-center shrink-0">
              <WhatsAppIcon className="h-8 w-8 text-[hsl(var(--whatsapp))]" size={32} />
            </div>
            <div className="text-center md:text-left">
              <h3 className="text-xl font-bold mb-2">Pro Tip: Maximize Your Sales with WhatsApp</h3>
              <p className="text-muted-foreground">
                Share your products to WhatsApp Status daily to keep your offerings visible. 
                The built-in rich previews show your product image, title, and price automatically—making 
                it easy for customers to see what you're selling and click to buy instantly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
            Seller FAQ
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Common questions from sellers about using the platform
          </p>
          
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-primary-foreground mb-4">
            Ready to Start Selling?
          </h2>
          <p className="text-primary-foreground/80 mb-8 max-w-xl mx-auto">
            Join our growing community of sellers. Create your free account today 
            and start reaching customers across the country.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/auth?mode=signup">
              <Button size="lg" variant="secondary" className="gap-2 w-full sm:w-auto">
                Sign Up as Seller <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10 w-full sm:w-auto">
                Contact Support
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Help Center Teaser */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-xl font-semibold mb-4">Still Have Questions?</h3>
          <p className="text-muted-foreground mb-6">
            Visit our Help Center for more detailed guides and tutorials.
          </p>
          <Link to="/help">
            <Button variant="outline">Visit Help Center</Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
